import {
	SOCIAL_AUTH_END_POINT,
	SOCIAL_AUTH_VERIFY_END_POINT,
	SOCIAL_AUTH_RESEND_END_POINT,
} from '../auth.api';
import { HTTP_STATUS_CODE } from '../../../enums/http';
import { ROUTE_NAME } from '../../../enums/route';
import { getErrorToastContent } from '../../../assets/js/errorMessage';

let storeInstance = null;
let routerInstance = null;


export function setStore(store) {
	storeInstance = store;
}

export function setRouter(router) {
	routerInstance = router;
}

// Requests owned by the Two-factor Authentication page. Their failures are
// rendered by that page (inline under the input, or its own toast), so the
// global handler must not navigate away or stack a duplicate toast on top.
const TWO_FACTOR_END_POINTS = [
	SOCIAL_AUTH_VERIFY_END_POINT,
	SOCIAL_AUTH_RESEND_END_POINT,
];

/**
 * เทียบ url ของ request กับ endpoint ที่กำหนด
 *
 * axios อาจส่ง url มาเป็น path ล้วน ('/backoffice/sessions') หรือเป็น absolute url
 * ('https://api.example.com/backoffice/sessions') ก็ได้ และอาจมี query string ต่อท้าย
 * จึงต้องตัด query/hash ทิ้งก่อนแล้วค่อยเทียบด้วย endsWith ไม่ใช้ === ตรงๆ
 *
 * @param {string} url       - url จาก error.response.config.url
 * @param {string} endPoint  - endpoint ที่ต้องการเทียบ
 * @returns {boolean}
 */
const isEndpoint = (url, endPoint) => {
	if (!url || !endPoint) {
		return false;
	}

	const pathOnly = String(url).split(/[?#]/)[0];

	return pathOnly.endsWith(endPoint);
};

const isTwoFactorEndpoint = (error) => {
	const url = error?.response?.config?.url || '';
	return TWO_FACTOR_END_POINTS.some((endPoint) => isEndpoint(url, endPoint));
};

/**
 * ชื่อ route ปัจจุบัน ใช้เทียบแทน path เพราะ redirect ทั้งหมดในไฟล์นี้ push ด้วย { name }
 * ถ้าวันหน้ามีการแก้ path ใน router loop guard จะยังทำงานถูกต้องอยู่
 */
const currentRouteName = () => routerInstance?.currentRoute?.name ?? null;

/**
 * redirect แบบกัน loop ไม่ push ซ้ำถ้าอยู่ที่ route ปลายทางอยู่แล้ว
 * @param {string} name - ROUTE_NAME ปลายทาง
 */
const redirectTo = (name) => {
	if (!routerInstance) {
		return;
	}

	if (currentRouteName() !== name) {
		routerInstance.push({ name });
	}
};

/**
 * จุดเดียวที่ interceptor นี้แสดง toast
 *
 * สำคัญ: ต้อง mark `error._toastShown` ทุกครั้งที่แสดง เพื่อให้ layer ที่อยู่เหนือขึ้นไป
 * (store/modules/toast.js) รู้ว่า error ก้อนนี้ถูกแจ้งผู้ใช้ไปแล้ว จะได้ไม่ขึ้น toast ซ้ำ
 * ตอนที่ store action catch error เดิมแล้วเรียก showMultipleErrorsToast ต่อ
 *
 * @param {object} error   - axios error object (จะถูก mutate เพื่อติด flag)
 * @param {object} toast   - payload ของ toast/showToast
 */
const showErrorToast = (error, toast) => {
	// eslint-disable-next-line require-atomic-updates
	error._toastShown = true;

	if (!storeInstance) {
		return;
	}

	storeInstance.dispatch('toast/showToast', {
		...toast,
		type: 'danger',
	});
};

export const interceptorResponse = (response) => {
	return response;
};

export const interceptorResponseError = async (error) => {
	// Network errors (offline, DNS failure, timeout, request canceled) have no
	// `response`. Bail out early so we don't crash on destructuring and don't
	// trigger router redirects that cause auth/profile loops.
	if (!error.response) {
		return Promise.reject(error);
	}

	const { response: { status: statusCode, data } } = error;

	// When responseType is 'blob', error body comes as a Blob instead of JSON
	// Parse it back to JSON so we can read error codes
	let errorData = data;
	if (data instanceof Blob) {
		try {
			const text = await data.text();
			errorData = JSON.parse(text);
		} catch {
			errorData = {};
		}
	}

	// Normalize errorData to always be a plain object
	// Handles empty response body cases where data is null, "", or undefined
	if (!errorData || typeof errorData !== 'object') {
		errorData = {};
	}

	// The 2FA page shows these errors itself - stay on the page and stay quiet.
	const isTwoFactorSelfHandled = isTwoFactorEndpoint(error);
	const requestUrl = error.response.config?.url || '';

	switch (statusCode) {
		// 400 - Bad Request
		case HTTP_STATUS_CODE.BAD_REQUEST:
			if (isTwoFactorSelfHandled) {
				break;
			}

			showErrorToast(error, {
				header: 'Bad Request (400)',
				content: getErrorToastContent(errorData, 'There was a problem with your request.'),
			});
			break;

		// 401 - Unauthorized
		case HTTP_STATUS_CODE.UNAUTHORIZED:
			redirectTo(ROUTE_NAME.LOGOUT);
			break;

		// 403 - Forbidden
		case HTTP_STATUS_CODE.FORBIDDEN:
			if (errorData.error === '2fa_required') {
				if (storeInstance) {
					storeInstance.dispatch('auth/setTwoFactorError', {
						message: errorData.message,
						redirect: routerInstance?.currentRoute?.path,
					});
				}

				redirectTo(ROUTE_NAME.TWO_FACTOR_AUTHENTICATION);
			} else if (isTwoFactorSelfHandled) {
				// Verify / resend rejected (wrong or expired code, locked out).
				// Handled inline by the 2FA page - do NOT bounce to /forbidden,
				// which would unmount the page and lose the entered code.
				break;
			} else if (errorData.error === 'order_visibility_forbidden') {
				showErrorToast(error, {
					header: 'ไม่สามารถเข้าถึง Order ได้',
					content: errorData.message,
				});
				redirectTo(ROUTE_NAME.FORBIDDEN);
			} else if (errorData.code === 'unauthorized_email_domain') {
				showErrorToast(error, {
					header: 'ไม่สามารถ Export ได้',
					content: errorData.message,
				});
			} else if (!isEndpoint(requestUrl, SOCIAL_AUTH_END_POINT)) {
				showErrorToast(error, {
					header: 'Forbidden (403)',
					content: errorData?.message || 'You do not have permission to perform this action.',
				});
				redirectTo(ROUTE_NAME.FORBIDDEN);
			}
			break;

		// 404 - Not Found
		case HTTP_STATUS_CODE.NOT_FOUND:
			showErrorToast(error, {
				header: 'Not Found (404)',
				content: errorData?.message || 'The requested resource could not be found.',
			});
			break;

		// 405 - Method Not Allowed
		case HTTP_STATUS_CODE.METHOD_NOT_ALLOWED:
			showErrorToast(error, {
				header: 'Method Not Allowed (405)',
				content: errorData?.message || 'Request method not allowed.',
			});
			break;

		// 409 - Conflict
		case HTTP_STATUS_CODE.CONFLICT:
			showErrorToast(error, {
				header: 'Conflict (409)',
				content: errorData?.message || 'Conflict occurred.',
			});
			break;

		// 422 - Unprocessable Entity
		case HTTP_STATUS_CODE.UNPROCESSABLE_ENTITY:
			if (isTwoFactorSelfHandled) {
				break;
			}

			showErrorToast(error, {
				header: 'Unprocessable Entity (422)',
				content: getErrorToastContent(errorData, 'Please verify your form data.'),
			});
			break;

		// 429 - Too Many Requests
		case HTTP_STATUS_CODE.TOO_MANY_REQUESTS:
			if (isTwoFactorSelfHandled) {
				break;
			}

			showErrorToast(error, {
				header: 'Too Many Requests (429)',
				content: errorData?.message || 'You are sending too many requests. Please wait a moment.',
			});
			break;

		// 5XX - Internal Server Error and other unexpected errors
		default:
			showErrorToast(error, {
				header: `Internal Server Error (${statusCode})`,
				content: errorData?.message || 'Please try again or contact support.',
			});
	}

	return Promise.reject(error);
};
